@let isMaximized = (isEditorMaximized$ | async) ?? false;
@let entitiesOverview = (entitiesOverview$ | async) ?? null;
<div class="top-bar">
  @if (!isMaximized) {
    <div class="tabs">
      <div [class.active]="currentTab === 'EDITOR'" (click)="switchTab('EDITOR')" class="clickable tab" translate="CALLS.EDITOR"></div>
      @if (call.isOutlineReady && call.accessLevel !== 'RESTRICTED') {
        <div [class.active]="currentTab === 'OUTLINE'" (click)="switchTab('OUTLINE')" class="clickable tab" translate="CALLS.OUTLINE"></div>
      }
      @if (showActionsTab) {
        <div [class.active]="currentTab === 'ACTIONS'" (click)="switchTab('ACTIONS')" class="clickable tab" translate="CALLS.ACTIONS"></div>
      }
    </div>
  }
  @if (currentTab === 'EDITOR' && !call.hasOnlyLegacyMinutes && entitiesOverview !== null && entitiesOverview.entities.length > 0) {
    <app-minutes-document-switcher
      class="document-switcher"
      [entities]="entitiesOverview.entities"
      [selectedCallEntityId]="selectedCallEntityId"
      [canManage]="canUserEdit"
      (selectionChange)="selectDocument($event)"
      (manage)="openManageEntities()"
    ></app-minutes-document-switcher>
  }
  @if (currentTab === 'EDITOR' && canUserEdit && !call.hasOnlyLegacyMinutes && entitiesOverview !== null && entitiesOverview.entities.length === 0) {
    <button
      mat-icon-button
      class="set-up-entities"
      (click)="openManageEntities()"
      [matTooltip]="'CALL_ENTITIES.SET_UP_TOOLTIP' | translate"
      [attr.aria-label]="'CALL_ENTITIES.SET_UP_TOOLTIP' | translate"
    >
      <mat-icon>account_tree</mat-icon>
    </button>
  }
  @if (call.isPostProcessingComplete) {
    <div class="buttons">
      @if (currentTab === 'OUTLINE') {
        @if (showExpandAll) {
          <button mat-icon-button (click)="expandAll()">
            <mat-icon fontSet="material-symbols-outlined">expand_all</mat-icon>
          </button>
        } @else {
          <button mat-icon-button (click)="collapseAll()">
            <mat-icon fontSet="material-symbols-outlined">collapse_all</mat-icon>
          </button>
        }
      }
      @if (currentTab === 'EDITOR' && !call.hasOnlyLegacyMinutes) {
        <button
          mat-icon-button
          class="find-replace-toggle"
          [class.active]="($safeNavigationMigration(findReplaceHost?.findReplaceOpen$) | async) ?? false"
          [attr.aria-pressed]="($safeNavigationMigration(findReplaceHost?.findReplaceOpen$) | async) ?? false"
          [matTooltip]="'EDITOR.FIND_REPLACE.OPEN_TOOLTIP' | translate"
          [attr.aria-label]="'EDITOR.FIND_REPLACE.OPEN_TOOLTIP' | translate"
          (click)="findReplaceHost?.toggleFindReplace()"
        >
          <mat-icon>search</mat-icon>
        </button>
      }
      @if (currentTab === 'EDITOR' && !call.hasOnlyLegacyMinutes && !isRestricted) {
        <button
          mat-icon-button
          class="revision-history-toggle"
          [class.active]="($safeNavigationMigration(tiptapEditor?.revisionHistoryOpen$) | async) ?? false"
          [attr.aria-pressed]="($safeNavigationMigration(tiptapEditor?.revisionHistoryOpen$) | async) ?? false"
          [matTooltip]="'EDITOR.REVISION_HISTORY.TOGGLE_TOOLTIP' | translate"
          [attr.aria-label]="'EDITOR.REVISION_HISTORY.TOGGLE_TOOLTIP' | translate"
          (click)="tiptapEditor?.toggleRevisionHistory()"
        >
          <mat-icon>history</mat-icon>
        </button>
      }
      @if (currentTab === 'EDITOR' && !call.hasOnlyLegacyMinutes && isMaximized) {
        @let zoomLevel = (zoomLevel$ | async) ?? 1;
        <button
          mat-icon-button
          (click)="zoomOut()"
          [disabled]="zoomLevel === editorZoomMin"
          [attr.aria-label]="'EDITOR.SETTINGS.ZOOM_OUT' | translate"
          [matTooltip]="'EDITOR.SETTINGS.ZOOM_OUT' | translate"
        >
          <mat-icon>zoom_out</mat-icon>
        </button>
        <button
          mat-button
          class="zoom-indicator"
          (click)="resetZoom()"
          [attr.aria-label]="'EDITOR.SETTINGS.RESET_ZOOM' | translate"
          [matTooltip]="'EDITOR.SETTINGS.RESET_ZOOM' | translate"
        >
          {{ zoomLevel * 100 | number: '1.0-0' }}%
        </button>
        <button
          mat-icon-button
          (click)="zoomIn()"
          [disabled]="zoomLevel === editorZoomMax"
          [attr.aria-label]="'EDITOR.SETTINGS.ZOOM_IN' | translate"
          [matTooltip]="'EDITOR.SETTINGS.ZOOM_IN' | translate"
        >
          <mat-icon>zoom_in</mat-icon>
        </button>
      }
      @if (canUserEdit && currentTab === 'EDITOR' && !call.hasOnlyLegacyMinutes) {
        <button
          mat-icon-button
          class="track-changes-toggle"
          [class.active]="$safeNavigationMigration(tiptapEditor?.trackChangesEnabled$) | async"
          [matTooltip]="($safeNavigationMigration(tiptapEditor?.trackChangesToggleLabel$) | async) ?? ''"
          [attr.aria-label]="($safeNavigationMigration(tiptapEditor?.trackChangesToggleLabel$) | async) ?? ''"
          (click)="tiptapEditor?.toggleTrackChanges()"
        >
          <mat-icon>track_changes</mat-icon>
        </button>
      }
      @if (currentTab === 'EDITOR' && hasCitations) {
        <button
          mat-icon-button
          [class.active]="showCitations"
          [attr.aria-pressed]="showCitations"
          (click)="toggleCitations()"
          [matTooltip]="(showCitations ? 'EDITOR.SETTINGS.HIDE_CITATIONS' : 'EDITOR.SETTINGS.SHOW_CITATIONS') | translate"
        >
          <mat-icon>link</mat-icon>
        </button>
      }
      @if (currentTab === 'EDITOR' && !isRestricted) {
        <button
          mat-icon-button
          [class.active]="showWordCount"
          [attr.aria-pressed]="showWordCount"
          (click)="toggleWordCount()"
          [matTooltip]="(showWordCount ? 'EDITOR.SETTINGS.HIDE_WORD_COUNT' : 'EDITOR.SETTINGS.SHOW_WORD_COUNT') | translate"
        >
          <mat-icon>title</mat-icon>
        </button>
      }
      @if (canExportCurrentTab) {
        <button mat-icon-button [matMenuTriggerFor]="menu">
          <mat-icon>download</mat-icon>
        </button>
        <mat-menu #menu="matMenu">
          <button mat-menu-item (click)="exportDocument('pdf')">PDF</button>
          <button mat-menu-item (click)="exportDocument('docx')">DOCX</button>
        </mat-menu>
      }
      @if (currentTab === 'EDITOR') {
        <button
          mat-icon-button
          class="maximize-toggle"
          [class.active]="isMaximized"
          [attr.aria-pressed]="isMaximized"
          [attr.aria-label]="(isMaximized ? 'EDITOR.SETTINGS.RESTORE_EDITOR' : 'EDITOR.SETTINGS.MAXIMIZE_EDITOR') | translate"
          (click)="toggleEditorMaximized()"
          [matTooltip]="(isMaximized ? 'EDITOR.SETTINGS.RESTORE_EDITOR' : 'EDITOR.SETTINGS.MAXIMIZE_EDITOR') | translate"
        >
          <mat-icon>{{ isMaximized ? 'close_fullscreen' : 'fullscreen' }}</mat-icon>
        </button>
      }
    </div>
  }
</div>

<div class="content">
  @if (isSensitiveModeEnabled) {
    <div class="sensitive-mode-placeholder"></div>
  } @else if (currentTab === 'EDITOR') {
    @if (call.hasOnlyLegacyMinutes) {
      <app-legacy-minutes-notice [call]="call" [canUserEdit]="canUserEdit"></app-legacy-minutes-notice>
    } @else if (call.accessLevel === 'RESTRICTED') {
      <app-restricted-minutes [call]="call"></app-restricted-minutes>
    } @else if (entitiesOverview !== null && selectedEntity !== null) {
      @if (selectedEntity.hasMinutes) {
        <!-- One entity at a time: keyed on the entity so that switching builds a new editor for the other document. -->
        @for (callEntityId of [selectedEntity.id]; track callEntityId) {
          <app-tiptap-editor
            [call]="call"
            [callEntityId]="callEntityId"
            [entityCitations]="(entityCitations$ | async) ?? null"
            [canUserEdit]="canUserEdit"
            [canUserSuggest]="canUserSuggest"
            [canUserComment]="canUserComment"
            [canResolveAnyThread]="canResolveAnyThread"
            (openAgendaUpload)="openAgendaUpload.emit()"
            (openDocument)="relayOpenDocument($event)"
            (openMeeting)="relayOpenMeeting($event)"
          ></app-tiptap-editor>
        }
      } @else {
        @let readiness = selectedEntityReadiness(entitiesOverview, selectedEntity);
        <div class="entity-minutes-placeholder">
          @if (selectedEntity.minutesStatus === 'ONGOING') {
            <app-progress-loader class="minutes-progress" indeterminate></app-progress-loader>
            <span class="loading-text">{{ 'CALL_ENTITIES.GENERATING' | translate: { name: selectedEntity.name } }}</span>
          } @else {
            <h2>{{ 'CALL_ENTITIES.NO_MINUTES_YET' | translate: { name: selectedEntity.name } }}</h2>
            @if (selectedEntity.minutesStatus === 'FAILED') {
              <p class="warning" translate="CALL_ENTITIES.GENERATION_FAILED"></p>
            }
            @if (readiness !== 'READY') {
              <p>{{ 'CALL_ENTITIES.READINESS.' + readiness | translate }}</p>
            }
            @if (canUserEdit) {
              <div class="placeholder-actions">
                <button mat-stroked-button (click)="openManageEntities()">
                  <mat-icon>tune</mat-icon>
                  <span translate="CALL_ENTITIES.MANAGE"></span>
                </button>
                <button mat-flat-button [disabled]="readiness !== 'READY'" (click)="generateSelectedEntityMinutes()">
                  <mat-icon>auto_awesome</mat-icon>
                  <span translate="CALL_ENTITIES.GENERATE_MINUTES"></span>
                </button>
              </div>
            }
          }
        </div>
      }
    } @else {
      <!-- Keyed on whether any item is restricted: that picks the editor's transport, fixed once it connects. -->
      @for (hasRestrictedMinuteItems of [call.hasRestrictedMinuteItems]; track hasRestrictedMinuteItems) {
        <app-tiptap-editor
          [call]="call"
          [canUserEdit]="canUserEdit"
          [canUserSuggest]="canUserSuggest"
          [canUserComment]="canUserComment"
          [canResolveAnyThread]="canResolveAnyThread"
          (openAgendaUpload)="openAgendaUpload.emit()"
          (openDocument)="relayOpenDocument($event)"
          (openMeeting)="relayOpenMeeting($event)"
        ></app-tiptap-editor>
      }
    }
  } @else if (currentTab === 'OUTLINE') {
    <app-outline [call]="call"></app-outline>
  } @else if (currentTab === 'ACTIONS') {
    <app-action-items [call]="call" [canUserEdit]="canUserEdit"></app-action-items>
  }
</div>
